import React from 'react';
import { BookMarked, CalendarDays, ClipboardCheck, History, LayoutDashboard, ShieldCheck, Stethoscope } from 'lucide-react';
import { ClinicalDemoProvider, useClinicalDemo } from '../../contexts/ClinicalDemoContext';
import { clinicalScopeNote } from '../../data/clinicalDemo';
import type { ClinicalSection, ClinicalWorkspace } from '../../types/clinicalDemo';
import { DemoShell, type ShellSection } from '../../components/demoShell/DemoShell';
import { WorkspaceChooser } from '../../components/demoShell/WorkspaceChooser';
import { ClinicalOverview } from '../../components/clinicalDemo/ClinicalOverview';
import { VisitTimeline } from '../../components/clinicalDemo/VisitTimeline';
import { DocumentationReview } from '../../components/clinicalDemo/DocumentationReview';
import { WorkflowReferences } from '../../components/clinicalDemo/WorkflowReferences';
import { ClinicalHistory } from '../../components/clinicalDemo/ClinicalHistory';
import { visitStatus } from '../../utils/clinicalDemo';
const workspaceLabels: Record<ClinicalWorkspace, string> = {
  clinician: 'Clinician Workspace',
  review: 'Authorized Review Workspace'
};
const views: Record<ClinicalSection, React.ComponentType> = {
  overview: ClinicalOverview,
  timeline: VisitTimeline,
  review: DocumentationReview,
  references: WorkflowReferences,
  history: ClinicalHistory
};
export function ClinicalDemoPage() {
  return <ClinicalDemoProvider data-id="element-2841" data-mp="1vk2kngkpje8b">
      <ClinicalDemo data-id="element-2842" data-mp="1virgvgr9i6h7" />
    </ClinicalDemoProvider>;
}
function ClinicalDemo() {
  const {
    state,
    dispatch,
    persistenceOk
  } = useClinicalDemo();
  const workspace = state.ui.workspace;
  if (!workspace) {
    return <WorkspaceChooser domainId="clinical" eyebrow="NOETIVRA CLINICAL · SCOPED INTERACTIVE DEMO" title="A clearer clinical picture." description="A clearer clinical picture through structured documentation and professional review." persistenceOk={persistenceOk} cards={[{
      id: 'clinician',
      title: 'Clinician Workspace',
      body: 'Record categorized documentation across two fictional visits and append addenda once a visit has been reviewed.',
      buttonLabel: 'Enter Clinician Workspace',
      icon: Stethoscope,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'clinician'
      })
    }, {
      id: 'review',
      title: 'Authorized Review Workspace',
      body: 'Review visit documentation, request clarification and record which entries each review covered.',
      buttonLabel: 'Enter Review Workspace',
      icon: ShieldCheck,
      onEnter: () => dispatch({
        type: 'enterWorkspace',
        workspace: 'review'
      })
    }]} footnote={<p className="rounded-lg border border-lavender/25 bg-lavender-pale px-4 py-3 text-sm leading-relaxed text-ink-dark" data-id="element-2844" data-mp="3dmxyppzcdjs7">{clinicalScopeNote}</p>} data-id="element-2843" data-mp="25a5n07dpy4jb" />;
  }
  const unreviewed = ['VIS-01', 'VIS-02'].filter(id => visitStatus(state, id) !== 'reviewed').length;
  const sections: ShellSection[] = [{
    id: 'overview',
    label: 'Overview',
    icon: LayoutDashboard
  }, {
    id: 'timeline',
    label: 'Visit Timeline',
    icon: CalendarDays
  }, {
    id: 'review',
    label: 'Documentation Review',
    icon: ClipboardCheck,
    count: workspace === 'review' ? unreviewed : undefined
  }, {
    id: 'references',
    label: 'Workflow References',
    icon: BookMarked
  }, {
    id: 'history',
    label: 'History & Controls',
    icon: History
  }];
  const current = state.ui.lastSection[workspace];
  const View = views[current] ?? ClinicalOverview;
  return <DemoShell domainId="clinical" productLabel="Noetivra Clinical · Scoped interactive demo" workspaceLabel={workspaceLabels[workspace]} sections={sections} current={current} onSelect={id => dispatch({
    type: 'setSection',
    section: id as ClinicalSection
  })} onSwitchWorkspace={() => dispatch({
    type: 'enterWorkspace',
    workspace: null
  })} persistenceOk={persistenceOk} extraNotice="Fictional patient and documentation. This demonstration illustrates documentation and review, not diagnosis." data-id="element-2845" data-mp="wfee6lwx84oe">
      <View data-id="element-2846" data-mp="212femm6yvyje" />
    </DemoShell>;
}